<template>
  <div :class="containerClass">
    <!-- Product Image - smoothly collapses on mobile when the panel is scrolled -->
    <div
      v-if="imageUrl"
      class="relative flex-shrink-0 bg-white overflow-hidden transition-all duration-300 ease-in-out"
      :class="showImage ? 'h-48 md:h-64 opacity-100' : 'h-0 opacity-0'"
    >
      <img
        :src="imageUrl"
        :alt="product.name"
        class="w-full h-full object-contain"
        @error="handleImageError"
      />
      <div
        v-if="product.badges && product.badges.length"
        class="absolute bottom-3 left-3 flex gap-2 flex-wrap max-w-[70%]"
      >
        <span
          v-for="(badge, idx) in product.badges"
          :key="idx"
          class="px-2 py-1 rounded text-xs font-bold"
          :style="{
            backgroundColor: badge.bg_color || '#14c240',
            color: badge.text_color || '#FFFFFF'
          }"
        >
          {{ badge.text }}
        </span>
      </div>
    </div>

    <!-- Title + price stay visible; only image/description collapse on scroll -->
    <div
      class="relative sticky top-0 z-30 bg-gray-50 p-6 pb-2 flex-shrink-0 border-b border-gray-100 product-info-header"
    >
      <div class="flex justify-between items-start mb-2">
        <h2
          class="text-2xl md:text-3xl font-serif font-bold text-gray-900 leading-tight break-words"
        >
          {{ product.name }}
        </h2>
      </div>
      <p
        :class="[
          'text-xl font-medium text-primary-600',
          productDescription ? 'mb-3' : 'mb-4'
        ]"
      >
        {{ formatPrice(displayPrice) }}
      </p>
      <!-- max-height collapse avoids iOS Safari grid 0fr flicker/clipping bugs -->
      <div
        v-if="productDescription"
        class="overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out"
        :class="
          showImage ? 'max-h-48 md:max-h-56 opacity-100' : 'max-h-0 opacity-0'
        "
      >
        <p
          class="text-sm md:text-base text-gray-600 leading-relaxed mb-4"
        >
          {{ productDescription }}
        </p>
      </div>

      <!-- Toggle centered on the header's bottom border (mobile only) -->
      <button
        v-if="collapsible && (imageUrl || productDescription)"
        type="button"
        @click="$emit('toggle-details')"
        class="md:hidden absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 z-10 flex items-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-700 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm transition-colors hover:border-primary-300 whitespace-nowrap"
      >
        <ChevronUp v-if="showImage" :size="14" />
        <ChevronDown v-else :size="14" />
        {{ showImage ? "Hide details" : "Show details" }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ChevronUp, ChevronDown } from "lucide-vue";

export default {
  name: "ProductInfo",
  components: {
    ChevronUp,
    ChevronDown
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    /** Show the mobile show/hide details toggle button. */
    collapsible: {
      type: Boolean,
      default: false
    },
    /** Total price including modifiers (updates like Add to Order button). When provided, overrides product.price. */
    totalPrice: {
      type: Number,
      default: null
    },
    /** When false, image is hidden (e.g. when right column is scrolled). */
    showImage: {
      type: Boolean,
      default: true
    },
    containerClass: {
      type: String,
      default:
        "w-full md:w-5/12 bg-gray-50 flex flex-col border-b md:border-b-0 md:border-r border-gray-300 min-h-0 shrink-0"
    }
  },
  computed: {
    ...mapGetters(["formatPrice"]),
    displayPrice() {
      const value =
        this.totalPrice != null && this.totalPrice !== ""
          ? Number(this.totalPrice)
          : this.product?.price ?? 0;
      return Number.isFinite(value) ? value.toFixed(2) : "0.00";
    },
    imageUrl() {
      return (
        this.product?.image ||
        this.product?.image_url ||
        this.product?.photo_thumbnail ||
        null
      );
    },
    productDescription() {
      return typeof this.product?.description === "string"
        ? this.product.description.trim()
        : "";
    }
  },
  methods: {
    handleImageError(event) {
      event.target.style.display = "none";
    }
  }
};
</script>

<style scoped>
.product-info-header {
  /* Sticky + animated siblings misbehave inside overflow-hidden modals on iOS Safari */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}
</style>
